import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Lightning } from "@phosphor-icons/react";
import ResearchTool from "@/pages/ResearchTool";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import { PrivacyPolicy, TermsOfService } from "@/pages/Legal";
import { AuthProvider, useAuth } from "@/lib/AuthProvider";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
    const location = useLocation();

    // Sync detection during render (NOT in useEffect) — the OAuth callback lands with a
    // #session_id=… fragment and we must exchange it BEFORE ProtectedRoute checks /auth/me.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            {/* Public pages — required by Google OAuth verification. Reviewers
                and unauthenticated visitors must be able to see what SerpScout
                does and read the legal pages without hitting an auth wall. */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy.html" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/terms-of-service.html" element={<TermsOfService />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
                path="/"
                element={<HomeGate />}
            />
            <Route
                path="/app"
                element={
                    <ProtectedRoute>
                        <ResearchTool />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// Root: show the public Landing page to unauthenticated visitors (so the
// authorized domain resolves to a non-login page for the Google OAuth
// verification reviewer), and show ResearchTool once the user is signed in.
function HomeGate() {
    const { user, loading } = useAuth();
    const location = useLocation();
    const injectedUser = location.state?.user;

    if (loading && !injectedUser) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center">
                <Lightning size={22} weight="bold" className="text-klein animate-pulse" />
                <div className="mt-3 font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                    Loading…
                </div>
            </div>
        );
    }

    if (user || injectedUser) {
        return <ResearchTool />;
    }
    return <Landing />;
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    // If AuthCallback just handed us the user via router state, skip the loading gate.
    const injectedUser = location.state?.user;

    if (loading && !injectedUser) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center">
                <Lightning size={22} weight="bold" className="text-klein animate-pulse" />
                <div className="mt-3 font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                    Loading…
                </div>
            </div>
        );
    }

    if (!user && !injectedUser) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                </AuthProvider>
            </BrowserRouter>
            <Toaster
                position="top-right"
                theme="light"
                toastOptions={{
                    style: {
                        borderRadius: 0,
                        border: "1px solid #0a0a0a",
                        fontFamily: "IBM Plex Sans, sans-serif",
                    },
                }}
            />
        </div>
    );
}

export default App;
